import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { useUser } from "@/zustand/store";
import { useSheetStore } from "@/zustand/sheetStore";
import { Edit, Plus } from "lucide-react";
import { useState } from "react";
import { useGetOTP, useUpdateUser } from "@/hooks/use-mutations";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useQueryClient } from "@tanstack/react-query";

const EditProfile = ({
  step,
  setStep,
  closeSheet,
}: {
  step: number;
  setStep: (step: number) => void;
  closeSheet: () => void;
}) => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { openSheet } = useSheetStore();
  const form = useForm({
    defaultValues: {
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
      contact: user.contact,
      otp: "",
    },
  });
  const { mutateAsync: getOtp, isPending: isGettingOtp } = useGetOTP();
  const { mutateAsync: updateProfile, isPending: isUpdating } = useUpdateUser();
  const [editingFields, setEditingFields] = useState(new Set<string>());
  const toggleEditing = (fieldName: string) => {
    setEditingFields((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fieldName)) {
        newSet.delete(fieldName);
      } else {
        newSet.add(fieldName);
      }
      return newSet;
    });
  };

  const onSubmit = async (data: any) => {
    try {
      await updateProfile(data);
      queryClient.invalidateQueries({ queryKey: ["user"] });
      closeSheet();
    } catch (e) {}
  };
  return (
    <div>
      <h1>Edit Profile</h1>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {step === 1 && (
            <>
              <div className="mt-4 space-y-4">
                <div className="flex flex-col gap-2.5 *:grow lg:flex-row">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-normal">
                          First Name
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              readOnly={!editingFields.has("firstName")}
                              className="rounded pr-4"
                              {...field}
                            />
                            <Button
                              variant={"ghost"}
                              type="button"
                              className="absolute top-1/2 right-2 size-6 -translate-y-1/2 rounded"
                              onClick={() => toggleEditing("firstName")}
                            >
                              <Edit size={16} strokeWidth={1.5} />
                            </Button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-normal">
                          Last Name
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              readOnly={!editingFields.has("lastName")}
                              className="rounded pr-4"
                              {...field}
                            />
                            <Button
                              variant={"ghost"}
                              type="button"
                              className="absolute top-1/2 right-2 size-6 -translate-y-1/2 rounded"
                              onClick={() => toggleEditing("lastName")}
                            >
                              <Edit size={16} strokeWidth={1.5} />
                            </Button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-normal">
                        Email
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            readOnly={!editingFields.has("email")}
                            className="rounded pr-4"
                            {...field}
                            onChange={(e) => {
                              const cleaned = e.target.value
                                .replace(/\s+/g, "")
                                .toLowerCase();
                              field.onChange(cleaned);
                            }}
                          />
                          <Button
                            variant={"ghost"}
                            type="button"
                            className="absolute top-1/2 right-2 size-6 -translate-y-1/2 rounded"
                            onClick={() => toggleEditing("email")}
                          >
                            <Edit size={16} strokeWidth={1.5} />
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contact"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-normal">
                        Contact
                      </FormLabel>
                      <FormControl>
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder={
                              field.value ? field.value : "Add Contact"
                            }
                            {...field}
                          />
                          <Button
                            variant={"outline"}
                            className="bg-transparent"
                            onClick={() => {
                              openSheet(
                                "addContact",
                                2,
                                { contact: field.value },
                                false,
                              );
                            }}
                          >
                            {field.value ? (
                              <Edit size={16} strokeWidth={1.5} />
                            ) : (
                              <Plus size={16} strokeWidth={1.5} />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-normal">
                        Password
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type="password"
                            readOnly={!editingFields.has("password")}
                            className="rounded pr-4 placeholder:text-white"
                            placeholder="••••••••"
                            {...field}
                          />
                          <Button
                            variant={"ghost"}
                            type="button"
                            className="absolute top-1/2 right-2 size-6 -translate-y-1/2 rounded"
                            onClick={() => toggleEditing("password")}
                          >
                            <Edit size={16} strokeWidth={1.5} />
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Button
                disabled={!form.formState.isDirty}
                type="button"
                onClick={async () => {
                  await getOtp({
                    emailAddress: form.getValues("email"),
                    purpose: "update",
                  });
                  setStep(2);
                }}
                className="text-bg-primary bg-primary-100 mt-8 w-full font-medium hover:bg-[#B3daff]"
                isLoading={isGettingOtp}
              >
                Continue
              </Button>
            </>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <>
              <h1 className="mt-6 text-2xl font-semibold">Verification Code</h1>
              <p className="text-sm leading-3.5 text-[#8C8C8C]">
                We’ve sent a 6-digit code to your email, enter the code below to
                verify
              </p>

              <div className="mt-6 flex w-full flex-col gap-2.5">
                <FormField
                  control={form.control}
                  name="otp"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Enter verification code</FormLabel>
                      <FormControl>
                        <InputOTP
                          autoFocus
                          autoComplete="off"
                          pattern={REGEXP_ONLY_DIGITS}
                          maxLength={6}
                          {...field}
                        >
                          <InputOTPGroup className="gap-3 *:!rounded *:border *:border-[#97A1AF] *:shadow-none">
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  disabled={isUpdating}
                  className="text-bg-primary bg-primary-100 mt-6 w-full"
                  type="submit"
                  isLoading={isUpdating}
                >
                  Save
                </Button>
              </div>
            </>
          )}
        </form>
      </Form>
    </div>
  );
};

export default EditProfile;
